import clsx from 'clsx';
import { useMemo, useState } from 'react';

import { FLAME_LABELS } from './combustionCorrosion';
import { ExploreChallenges } from './ExploreChallenges';
import {
  BURN_FUELS,
  DEFAULT_FUEL_BURN_SETUP,
  DEFAULT_FUEL_ID,
  FUEL_BURN_CHALLENGES,
  FUEL_BURN_FIELDS,
  FUEL_BURN_QUESTION,
  type FuelBurnSetup,
  burnFuelById,
  calculateFuelBurn,
} from './fuelBurnExplore';
import { ModelInputPanel, WorkedStepsPanel } from './ModelInputs';
import { formatModelNumber } from './modelInputContract';

const OXYGEN_MAX_G = FUEL_BURN_FIELDS.find((field) => field.key === 'oxygenMassG')?.max ?? 0;

function Bar({ label, grams, scale, tone, testId }: { label: string; grams: number; scale: number; tone: string; testId: string }) {
  return (
    <div>
      <p className="flex justify-between gap-2 text-xs font-extrabold text-ink">
        <span>{label}</span>
        <span data-testid={testId}>{formatModelNumber(grams, 2)} g</span>
      </p>
      <div className="mt-1 h-3 overflow-hidden rounded-full bg-surface" aria-hidden="true">
        <div className={clsx('h-full rounded-full', tone)} style={{ width: `${scale > 0 ? Math.min(grams / scale, 1) * 100 : 0}%` }} />
      </div>
    </div>
  );
}

/**
 * Explore: "Burn a fuel" (CT-INTERACT). The learner sets the fuel, its mass and the oxygen; one
 * result drives the oxygen bars, the product bars, the flame badge and the worked calculation.
 */
export function FuelBurnExplorer() {
  const [fuelId, setFuelId] = useState(DEFAULT_FUEL_ID);
  const [setup, setSetup] = useState<FuelBurnSetup>(DEFAULT_FUEL_BURN_SETUP);
  const fuel = burnFuelById(fuelId);
  const result = useMemo(() => calculateFuelBurn(fuel, setup), [fuel, setup]);
  const oxygenScale = Math.max(setup.oxygenMassG, result.oxygenNeededG);
  const productScale = (result.carbonDioxideG ?? 0) + (result.waterG ?? 0);
  const presets = [
    { id: 'plenty', label: 'Plenty of oxygen', oxygen: Math.ceil(result.oxygenNeededG * 20) / 10 },
    { id: 'just-enough', label: 'Just enough', oxygen: Math.ceil(result.oxygenNeededG * 10) / 10 },
    { id: 'half', label: 'Half the oxygen', oxygen: Math.round(result.oxygenNeededG * 5) / 10 },
    { id: 'none', label: 'No oxygen', oxygen: 0 },
  ];

  return (
    <section
      className="my-6 rounded-[2rem] border border-hairline bg-canvas-pure p-5 shadow-card-soft sm:p-6 lg:p-8"
      aria-labelledby="fuel-burn-heading"
    >
      <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-brand-coral">Explore</p>
      <h2 id="fuel-burn-heading" className="mt-2 text-2xl font-extrabold text-ink">
        Burn a fuel
      </h2>
      <p className="mt-1 max-w-3xl text-sm font-medium text-slate2">
        Choose a fuel, its mass and how much oxygen it gets. The model uses the balanced equation to check whether there is
        enough oxygen for complete combustion and, if there is, how much carbon dioxide and water form.
      </p>

      <div className="mt-5 grid gap-5 lg:grid-cols-2">
        <div className="min-w-0 space-y-4">
          <div>
            <label className="block text-sm font-extrabold text-ink">
              Fuel
              <select
                aria-label="Fuel"
                value={fuelId}
                onChange={(event) => setFuelId(event.target.value)}
                className="mt-2 w-full rounded-2xl border border-hairline bg-surface px-4 py-3 font-bold outline-none focus:border-brand-sky"
              >
                {BURN_FUELS.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.reactants[0].name}
                  </option>
                ))}
              </select>
            </label>
            <p className="mt-2 break-words font-mono text-sm font-extrabold text-ink" data-testid="fuel-equation">
              {result.equation}
            </p>
          </div>
          <ModelInputPanel
            legend="Set up the burn"
            fields={FUEL_BURN_FIELDS}
            values={setup}
            onChange={(next) => setSetup(next as FuelBurnSetup)}
            sliderKey="oxygenMassG"
          />
          <div className="flex flex-wrap gap-2" aria-label="Oxygen presets">
            {presets.map((preset) => (
              <button
                key={preset.id}
                type="button"
                disabled={preset.oxygen > OXYGEN_MAX_G}
                onClick={() => setSetup({ ...setup, oxygenMassG: preset.oxygen })}
                className="rounded-full border border-hairline bg-surface px-3 py-1.5 text-xs font-extrabold text-ink hover:border-brand-sky/50 disabled:cursor-not-allowed disabled:opacity-40"
              >
                {preset.label}
              </button>
            ))}
          </div>
          <ExploreChallenges question={FUEL_BURN_QUESTION} challenges={FUEL_BURN_CHALLENGES} result={result} />
          <div className="space-y-3 rounded-3xl border border-hairline bg-canvas-pure p-4" role="group" aria-label="Oxygen and products">
            <p
              className={clsx(
                'inline-flex rounded-full px-3 py-1 text-xs font-extrabold',
                result.flame === 'blue' ? 'bg-wash-sky text-ink' : result.flame === 'yellow' ? 'bg-wash-sunshine text-ink' : 'bg-surface text-slate2',
              )}
              data-testid="fuel-flame"
            >
              {result.flame ? `Expected flame: ${FLAME_LABELS[result.flame]}` : 'No flame: nothing burns'}
            </p>
            <Bar label="Oxygen needed" grams={result.oxygenNeededG} scale={oxygenScale} tone="bg-slate2" testId="fuel-oxygen-needed" />
            <Bar label="Oxygen available" grams={setup.oxygenMassG} scale={oxygenScale} tone="bg-brand-sky" testId="fuel-oxygen-available" />
            {result.carbonDioxideG !== null && result.waterG !== null ? (
              <>
                <Bar label="Carbon dioxide formed" grams={result.carbonDioxideG} scale={productScale} tone="bg-brand-coral" testId="fuel-co2" />
                <Bar label="Water formed" grams={result.waterG} scale={productScale} tone="bg-brand-mint" testId="fuel-water" />
              </>
            ) : null}
          </div>
        </div>
        <WorkedStepsPanel
          title="How the model works it out"
          steps={result.steps}
          stepTestIdPrefix="fuel-step"
          result={
            result.kind === 'complete'
              ? `Result: complete combustion · ${formatModelNumber(result.carbonDioxideG ?? 0, 2)} g CO₂ + ${formatModelNumber(result.waterG ?? 0, 2)} g H₂O · ${formatModelNumber(result.oxygenLeftG ?? 0, 2)} g O₂ left over`
              : result.kind === 'incomplete'
                ? `Result: incomplete combustion · only ${formatModelNumber(result.oxygenShare * 100, 0)} % of the oxygen needed`
                : 'Result: no combustion · no oxygen'
          }
          resultTestId="fuel-result"
          limitNote={result.limitNote}
        />
      </div>
      <p className="mt-4 text-xs font-medium leading-5 text-slate2">
        Model boundaries: the fuel and oxygen are mixed perfectly and the water is counted as a gas, as in the equation. Real
        flames lose heat and can make some carbon monoxide even with plenty of air. Burning needs a teacher and a risk
        assessment.
      </p>
    </section>
  );
}
